import { fmtNum } from "./toEnglishDigits";
import { formatPhoneForWhatsApp } from "./phoneFormat";
import { computeDepartureRule } from "./date";
import { getBookingAirline, getBookingFlightNumber } from "./airline";
import { computeSeats } from "./seats";

// Re-export so existing imports keep working — single source of truth is phoneFormat.js
export { formatPhoneForWhatsApp };

const SEP = "━━━━━━━━━━━━━━━━━━━━";

// Coerce to a trimmed non-empty string or "—".
// Treats "", null, undefined, and " " as empty; keeps 0 as "0".
function safeVal(v) {
  if (v === null || v === undefined) return "—";
  const s = String(v).trim();
  return s.length > 0 ? s : "—";
}

// Aggregate multi-route bookings (routes[]) — first pickup → last destination, union of cars.
// Falls back to flat booking fields when routes[] is absent.
function summariseRoutes(booking) {
  const routes = Array.isArray(booking?.routes) ? booking.routes : [];
  if (routes.length > 0) {
    const first = routes[0] || {};
    const last = routes[routes.length - 1] || {};
    const pickup = safeVal(first.pickup_location || booking?.pickup_location);
    const dest = safeVal(last.destination || booking?.destination);
    const carSet = new Set();
    for (const r of routes) {
      for (const c of (r.cars || [])) {
        const nm = c.car_name_ar || c.car_type_name || c.car_type;
        const q = Number(c.quantity || 1);
        if (nm) carSet.add(q > 1 ? `${nm} × ${fmtNum(q)}` : nm);
      }
    }
    const carsLine = carSet.size > 0
      ? [...carSet].join(" · ")
      : safeVal(booking?.car_name_ar || booking?.car_type_name || booking?.car_type);
    return { pickup, dest, carsLine, totalPax: safeVal(booking?.passengers) };
  }
  return {
    pickup: safeVal(booking?.pickup_location),
    dest: safeVal(booking?.destination || booking?.dropoff_location),
    carsLine: safeVal(booking?.car_name_ar || booking?.car_type_name || booking?.car_type),
    totalPax: safeVal(booking?.passengers ?? booking?.passenger_count ?? booking?.pax),
  };
}

// Build a plain-text (no emoji) driver-assignment WhatsApp message.
// Uses ▪ • ━ characters + WhatsApp *bold* — renders on every device / font.
//
// Signature: buildDriverAssignmentMessage(booking, driver, { isExternal } = {})
// (Legacy `slot` field is still accepted but no longer read — wage line removed.)
export function buildDriverAssignmentMessage(booking, driver, opts = {}) {
  const isExternal = !!opts.isExternal;
  const capacityMap = opts.capacityMap || {};
  const driverName = driver?.name || "";

  const { pickup, dest, carsLine, totalPax: fallbackPax } = summariseRoutes(booking);
  // Passenger count = value the company entered (booking.passengers). Never
  // use capacity × quantity here — that stays PDF-only (per 2026-02 spec).
  const bookingPax = Number(booking?.passengers || 0);
  const totalPax = bookingPax > 0 ? fmtNum(bookingPax) : fallbackPax;
  const clientName = safeVal(booking?.client_name || booking?.user_name || booking?.passenger_name);
  const clientPhone = safeVal(booking?.client_phone || booking?.user_phone || booking?.passenger_phone);

  const flightNo = getBookingFlightNumber(booking);
  const airline = getBookingAirline(booking);

  const num = booking?.booking_number || booking?.seq_number || booking?.internal_serial;
  const date = (booking?.trip_date || "").toString().slice(0, 10);
  const time = (booking?.trip_time || "").toString().slice(0, 5);

  // Departure timing engine — 8 named rules + generic airport fallback.
  // Admin override on the booking (`departure_time_driver`) takes precedence.
  const driverDep = computeDepartureRule(pickup, dest, time, booking?.departure_time_driver);

  const lines = [];
  lines.push("*【 مراسم الزوار 】*");
  lines.push("");
  lines.push(`مرحباً ${driverName}،`);
  lines.push("");
  lines.push(isExternal
    ? "تفاصيل الرحلة الموكلة إليك من شركة مراسم الزوار:"
    : "تم تعيينك على حجز جديد. تفاصيل الرحلة:");
  lines.push("");
  lines.push(SEP);
  lines.push(`▪ *رقم الحجز:*  #${safeVal(num)}`);
  lines.push(`▪ *التاريخ:*    ${safeVal(date)}`);
  if (driverDep) {
    const suffix = driverDep.isOverride ? "" : ` (${driverDep.label})`;
    lines.push(`▪ *وقت التحرك:* ${driverDep.time}${suffix}`);
    lines.push(`▪ *موعد الحجز:* ${safeVal(time)}`);
  } else {
    lines.push(`▪ *الوقت:*     ${safeVal(time)}`);
  }
  // Flight number sits in the top summary block, right after the time lines.
  if (flightNo) {
    if (airline) lines.push(`▪ *شركة الطيران:* ${airline}`);
    lines.push(`▪ *رقم الرحلة:* ${flightNo}`);
  }
  lines.push(SEP);
  lines.push("");
  lines.push("*بيانات العميل*");
  lines.push(`•  الاسم:        ${clientName}`);
  lines.push(`•  الجوال:       ${clientPhone}`);
  lines.push(`•  عدد الركاب:  ${totalPax}`);
  lines.push("");
  lines.push("*تفاصيل المسار*");
  lines.push(`•  من:  ${pickup}`);
  lines.push(`•  إلى: ${dest}`);
  lines.push("");
  lines.push("*تفاصيل السيارة*");
  lines.push(`•  نوع السيارة: ${carsLine}`);
  lines.push("");
  lines.push(SEP);
  lines.push("يرجى التواصل مع العميل لتأكيد الرحلة.");
  lines.push("");
  lines.push("مع تحيات فريق مراسم الزوار.");
  return lines.join("\n");
}

// Build the ready-to-open wa.me URL for a driver assignment.
export function buildDriverWhatsAppUrl(booking, driver, opts = {}) {
  const phone = formatPhoneForWhatsApp(driver?.phone);
  if (!phone) return null;
  const msg = buildDriverAssignmentMessage(booking, driver, opts);
  return `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`;
}

// Fire-and-open wa.me for the given booking + driver.
// Returns true if a window was opened, false otherwise.
export function openDriverWhatsApp(booking, driver, opts = {}) {
  const url = buildDriverWhatsAppUrl(booking, driver, opts);
  if (!url) return false;
  const w = window.open(url, "_blank", "noopener,noreferrer");
  return !!w;
}
